import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { PillLink } from "@/components/common/PillLink";
import { NAV_LINKS, STUDENT_LOGIN_URL } from "@/lib/site";

const linkCls = ({ isActive }) =>
  `rounded-full px-4 py-2 text-[15px] font-bold transition-colors duration-200 ${
    isActive ? "bg-ink text-cream" : "text-ink hover:bg-white"
  }`;

const StudentLogin = ({ testid, className = "" }) => (
  <a
    href={STUDENT_LOGIN_URL || "#"}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[15px] font-bold text-ink underline-offset-4 hover:underline ${className}`}
  >
    <LogIn className="h-4 w-4" aria-hidden="true" /> Student Login
    <span className="sr-only">(opens in a new tab)</span>
  </a>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl bg-cream/95 transition-shadow duration-300 ${scrolled ? "shadow-soft" : ""}`}
      data-testid="site-header"
    >
      <div className="container-x max-w-[1760px] flex h-20 items-center justify-between gap-4">
        <Logo />
        <nav aria-label="Main" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} end={l.to === "/"} className={linkCls} data-testid={l.testid}>
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <StudentLogin testid="nav-student-login" />
          <PillLink to="/contact#enquire" variant="lemon" data-testid="nav-enrol-now" className="py-2.5">
            Enrol Now
          </PillLink>
        </div>
        <button
          type="button"
          className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-ink/10 bg-white"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <nav id="mobile-menu" aria-label="Mobile" className="lg:hidden border-t border-ink/10 bg-cream" data-testid="mobile-menu">
          <ul className="container-x flex flex-col gap-1 py-5">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} end={l.to === "/"} className={(s) => `block ${linkCls(s)} py-3`} data-testid={`mobile-${l.testid}`}>
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li className="flex flex-wrap items-center gap-3 pt-3">
              <PillLink to="/contact#enquire" variant="lemon" data-testid="mobile-nav-enrol-now">Enrol Now</PillLink>
              <StudentLogin testid="mobile-nav-student-login" />
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
};
